import type { AllocationInput, ExpenseInput, Transaction } from "../types";
import { formatMinor, formatMoney, roundToCents } from "./money-math";

export function minorAmount(value: string): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  const result = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(result) ? result : null;
}

/** Exact largest-remainder distribution of integer cents; input order breaks ties. */
export function distributeMinor(total: number, weights: number[]): number[] {
  if (!Number.isSafeInteger(total) || weights.some(n => !Number.isSafeInteger(n) || n < 0))
    throw new Error("Use whole minor units and nonnegative allocation weights.");
  const sum = weights.reduce((value, weight) => value + BigInt(weight), 0n);
  if (!sum) {
    if (total) throw new Error("Choose a category for an adjustment with no positive item value.");
    return weights.map(() => 0);
  }
  const magnitude = BigInt(Math.abs(total));
  const products = weights.map(weight => magnitude * BigInt(weight));
  const parts = products.map(value => Number(value / sum));
  const ranked = products.map((value, index) => ({ index, remainder: value % sum }))
    .sort((a, b) => a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1);
  const spare = Math.abs(total) - parts.reduce((value, part) => value + part, 0);
  for (let i = 0; i < spare; i++) parts[ranked[i].index]++;
  return parts.map(value => total < 0 && value ? -value : value);
}

/** Shared by the form and API: no rounding or silently filling an unbalanced split. */
export function checkedAllocations(input: Pick<ExpenseInput, "allocations" | "amount" | "reportableAmount" | "type">): AllocationInput[] | undefined {
  if (input.allocations === undefined) return undefined;
  if (input.type === "Transfer") throw new Error("Transfers cannot be split into categories.");
  if (!Array.isArray(input.allocations) || input.allocations.length < 1 || input.allocations.length > 50)
    throw new Error("Use between 1 and 50 category rows.");
  let actual = 0, reportable = 0;
  const parts = input.allocations.map(part => {
    if (!part || typeof part.category !== "string" || !part.category.trim() || part.category.length > 100 || typeof part.subcategory !== "string" || part.subcategory.length > 100)
      throw new Error("Choose a category for every split row.");
    const amount = typeof part.amount === "string" ? minorAmount(part.amount) : null;
    const share = part.reportableAmount === undefined ? amount : typeof part.reportableAmount === "string" ? minorAmount(part.reportableAmount) : null;
    if (amount === null || amount <= 0 || share === null || share > amount)
      throw new Error("Each category needs a positive amount and a household share between zero and that amount.");
    actual += amount;
    reportable += share;
    return { category: part.category.trim(), subcategory: part.subcategory.trim(), amount: formatMinor(amount), ...(part.reportableAmount !== undefined ? { reportableAmount: formatMinor(share) } : {}) };
  });
  if (actual !== minorAmount(input.amount)) throw new Error("Category amounts must add up to the purchase total.");
  if (reportable !== minorAmount(input.reportableAmount ?? input.amount)) throw new Error("Category household shares must add up to the household total.");
  return parts;
}

export const transactionAllocations = (t: Transaction) => t.allocations?.length ? t.allocations : [{ category: t.category, subcategory: t.subcategory, amount: t.amount, reportableAmount: t.reportableAmount, amountBase: t.amountBase, reportableAmountBase: t.reportableAmountBase }];
export const allocationDraft = (t: Transaction): AllocationInput[] | undefined => t.allocations?.length ? t.allocations.map(p => ({ category: p.category, subcategory: p.subcategory, amount: formatMoney(p.amount), ...(p.reportableAmount !== p.amount || t.reportableAmount !== t.amount ? { reportableAmount: formatMoney(p.reportableAmount) } : {}) })) : undefined;
/** Only for reporting. Ledger lists retain the original, single transaction. */
export const reportingTransactions = (transactions: Transaction[]): Transaction[] => transactions.flatMap(t => transactionAllocations(t).map(p => ({ ...t, ...p, allocations: undefined })));
export const matchesCategory = (t: Transaction, category: string, subcategory: string | null = null) => transactionAllocations(t).some(p => (category === "All" || p.category === category) && (subcategory === null || (p.subcategory || "Unspecified") === subcategory));
export function categoryLabel(t: Transaction) {
  const names = [...new Set(transactionAllocations(t).map(p => p.subcategory || p.category))];
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}
export const allocationSignature = (t: Transaction) => JSON.stringify(transactionAllocations(t).map(p => [p.category, p.subcategory, roundToCents(p.amount), roundToCents(p.reportableAmount)]));
